import type {
  DimensionCatalogEntry,
  DimensionSeed,
  MetricDirection,
  MetricRegistryAggregationRule,
  MetricRegistryAppliesTo,
  MetricRegistrySourceOfTruthClass,
  MetricTemporalType,
} from './metric-registry';
import type { GovernedMeasureIdentity } from './governed-measure-identity';

/**
 * #5544 - THE SG&A COST-POOL DIMENSION, as one governed declaration.
 *
 * ChatGPT AMEND (#5544) §1-§2: one canonical SG&A spend measure carries
 * `sga_cost_pool` as a governed DIMENSION rather than forking a second metric
 * identity, and the dimension's members are the EXISTING SG&A cost-pool/lever
 * taxonomy (apps/api/src/decision-modules/sga-spend/sga-spend.types.ts -
 * `SGA_SPEND_LEVER_IDS`, including the #7835 non-addressable reconciliation pool), never invented here.
 * `packages/lib` cannot import from `apps/api` (wrong dependency direction),
 * so the lever ids are mirrored as literal string values -
 * `sga-spend.cost-pool-taxonomy-parity.spec.ts` in the sga-spend module pins
 * byte-identical parity against the real `SGA_SPEND_LEVER_IDS` array so this
 * mirror can never silently drift.
 */
export const SGA_COST_POOL_DIMENSION = 'sga_cost_pool' as const;
export const SGA_COST_POOL_TAXONOMY_VERSION = 'sga_cost_pool_taxonomy_v1' as const;

/**
 * Mirrors apps/api SGA_SPEND_LEVER_IDS exactly - parity-pinned, see file
 * header. #5571 UNIT V1 RULING extended this from 8 to 22 pools: the first 8
 * are unchanged (no renames), the 14 after them are the #5571 additions, in
 * the ruling's exact order. See
 * apps/api/src/decision-modules/sga-spend/sga-spend.types.ts for the ruling
 * detail (branch-occupancy merge into real_estate_footprint, insurance split
 * into premiums/claims-SIR, and the purchased-transportation /
 * warehouse-dock-labor exclusion).
 */
export const SGA_COST_POOL_LEVER_MEMBER_IDS = [
  // --- Original 8 (unchanged) ---
  'professional_fees',
  'outside_contractors',
  'indirect_procurement',
  'travel_and_expense',
  'shared_services',
  'tech_software',
  'real_estate_footprint',
  'marketing_spend',
  // --- #5571 additions (14) ---
  'personnel_sales_account_management',
  'personnel_operations_carrier_reps',
  'personnel_executive_corporate',
  'personnel_benefits_payroll_taxes_equity',
  'personnel_hr_recruiting_training',
  'tech_telecom_data',
  'tech_cloud_infrastructure',
  'finance_accounting_tax_treasury',
  'legal_regulatory_compliance',
  'insurance_premiums',
  'cargo_claims_self_insured_retention',
  'bad_debt_credit_losses',
  'office_purchased_services',
  'depreciation_amortization_non_operating',
  'software_amortization',
] as const;

export type SgaCostPoolLeverMemberId = (typeof SGA_COST_POOL_LEVER_MEMBER_IDS)[number];

/**
 * §2: "an explicit unallocated_or_unmapped member where source rows cannot be
 * assigned... Unknown pool ids fail admission or land in the named unmapped
 * member according to one declared policy; they never vanish."
 */
export const SGA_COST_POOL_UNALLOCATED_MEMBER_ID = 'unallocated_or_unmapped' as const;

export const SGA_COST_POOL_MEMBER_IDS = [
  ...SGA_COST_POOL_LEVER_MEMBER_IDS,
  SGA_COST_POOL_UNALLOCATED_MEMBER_ID,
] as const;

export type SgaCostPoolMemberId = (typeof SGA_COST_POOL_MEMBER_IDS)[number];

/** Declaration-owned labels - mirrors apps/api SGA_SPEND_LEVER_CONFIGS.displayLabel. */
export const SGA_COST_POOL_MEMBER_LABELS: Readonly<Record<SgaCostPoolMemberId, string>> =
  Object.freeze({
    professional_fees: 'Professional fees',
    outside_contractors: 'Outside contractors',
    indirect_procurement: 'Indirect procurement',
    travel_and_expense: 'Travel and expense',
    shared_services: 'Shared services',
    tech_software: 'Technology and software',
    // #5571 UNIT V1 RULING: branch occupancy MERGED into this pool - label
    // extended, no rename. Mirrors sga-spend.types.ts#real_estate_footprint.
    real_estate_footprint: 'Real estate, facilities and branch occupancy',
    marketing_spend: 'Marketing and demand generation',
    personnel_sales_account_management: 'Sales and account-management personnel',
    personnel_operations_carrier_reps: 'Operations and carrier-representative personnel',
    personnel_executive_corporate: 'Executive and corporate personnel',
    personnel_benefits_payroll_taxes_equity: 'Benefits, payroll taxes and equity compensation',
    personnel_hr_recruiting_training: 'HR, recruiting and training personnel',
    tech_telecom_data: 'Telecom and data connectivity',
    tech_cloud_infrastructure: 'Cloud infrastructure',
    finance_accounting_tax_treasury: 'Finance, accounting, tax and treasury',
    legal_regulatory_compliance: 'Legal, regulatory and compliance',
    // #5571 UNIT V1 RULING: insurance SPLIT into premiums (contractual) and
    // claims/SIR (loss-driven) - see cargo_claims_self_insured_retention below.
    insurance_premiums: 'Insurance premiums',
    cargo_claims_self_insured_retention: 'Cargo claims and self-insured retention',
    bad_debt_credit_losses: 'Bad debt and credit losses',
    office_purchased_services: 'Office supplies and purchased services',
    depreciation_amortization_non_operating: 'Depreciation and amortization, non-operating',
    software_amortization: 'Software amortization',
    unallocated_or_unmapped: 'Unallocated or unmapped',
  });

/** Mirrors apps/api SGA_SPEND_LEVER_CONFIGS.costPoolId - the module's own internal pool key. */
const SGA_COST_POOL_SOURCE_LINE_KEY_BY_LEVER: Readonly<Record<SgaCostPoolLeverMemberId, string>> =
  Object.freeze({
    professional_fees: 'corporate_professional_fees',
    outside_contractors: 'corporate_outside_contractors',
    indirect_procurement: 'corporate_indirect_procurement',
    travel_and_expense: 'corporate_travel_expense',
    shared_services: 'corporate_shared_services',
    tech_software: 'corporate_tech_software',
    real_estate_footprint: 'corporate_real_estate_footprint',
    marketing_spend: 'corporate_marketing_spend',
    personnel_sales_account_management: 'corporate_personnel_sales_account_management',
    personnel_operations_carrier_reps: 'corporate_personnel_operations_carrier_reps',
    personnel_executive_corporate: 'corporate_personnel_executive_corporate',
    personnel_benefits_payroll_taxes_equity: 'corporate_personnel_benefits_payroll_taxes_equity',
    personnel_hr_recruiting_training: 'corporate_personnel_hr_recruiting_training',
    tech_telecom_data: 'corporate_tech_telecom_data',
    tech_cloud_infrastructure: 'corporate_tech_cloud_infrastructure',
    finance_accounting_tax_treasury: 'corporate_finance_accounting_tax_treasury',
    legal_regulatory_compliance: 'corporate_legal_regulatory_compliance',
    insurance_premiums: 'corporate_insurance_premiums',
    cargo_claims_self_insured_retention: 'corporate_cargo_claims_self_insured_retention',
    bad_debt_credit_losses: 'corporate_bad_debt_credit_losses',
    office_purchased_services: 'corporate_office_purchased_services',
    depreciation_amortization_non_operating: 'corporate_depreciation_amortization_non_operating',
    software_amortization: 'corporate_software_amortization',
  });

export const SGA_COST_POOL_DIMENSION_CATALOG: DimensionCatalogEntry = {
  dimension: SGA_COST_POOL_DIMENSION,
  label: 'SG&A cost pool',
  // See metric-query-compiler.ts#MetricFactDimensionColumn:costPoolKey - declared,
  // not yet a live GovernedMetricFact column.
  physicalColumn: 'costPoolKey',
  rollups: [
    { rollup: 'sum', label: 'By cost pool' },
    { rollup: 'enterprise', label: 'Total across cost pools', parentRollup: 'sum' },
  ],
  source:
    'apps/api/src/decision-modules/sga-spend/sga-spend.types.ts#SGA_SPEND_LEVER_IDS + ' +
    'SGA_SPEND_LEVER_CONFIGS.costPoolId (22-lever taxonomy as of #5571 UNIT V1, ' +
    'originally 8); unallocated_or_unmapped added by #5544',
};

export const SGA_COST_POOL_DIMENSION_SEED: DimensionSeed = {
  dimension: SGA_COST_POOL_DIMENSION,
  label: 'SG&A cost pool',
  source: SGA_COST_POOL_DIMENSION_CATALOG.source,
  members: [
    ...SGA_COST_POOL_LEVER_MEMBER_IDS.map((memberKey) => ({
      dimension: SGA_COST_POOL_DIMENSION,
      memberKey,
      label: SGA_COST_POOL_MEMBER_LABELS[memberKey],
      rollup: 'sum',
      sourceLineKeys: [SGA_COST_POOL_SOURCE_LINE_KEY_BY_LEVER[memberKey]],
      attributes: {
        leverId: memberKey,
        costPoolId: SGA_COST_POOL_SOURCE_LINE_KEY_BY_LEVER[memberKey],
      },
    })),
    {
      dimension: SGA_COST_POOL_DIMENSION,
      memberKey: SGA_COST_POOL_UNALLOCATED_MEMBER_ID,
      label: SGA_COST_POOL_MEMBER_LABELS[SGA_COST_POOL_UNALLOCATED_MEMBER_ID],
      rollup: 'sum',
      sourceLineKeys: [],
      attributes: { policy: 'unmapped_source_rows_land_here_never_dropped' },
    },
  ],
};

/**
 * §2 mapping-receipt policy: exactly one governed pool id per recognized source
 * key, closed over the declared taxonomy - never a keyword/string-match guess.
 * An unrecognized leverId/costPoolId lands in `unallocated_or_unmapped` with a
 * named reason rather than vanishing or defaulting to a real pool.
 */
export const SGA_COST_POOL_MAPPING_REASONS = [
  'matched_lever_id',
  'matched_cost_pool_id',
  'unmapped_no_source_identity',
  'unmapped_unknown_identity',
] as const;
export type SgaCostPoolMappingReason = (typeof SGA_COST_POOL_MAPPING_REASONS)[number];

export type SgaCostPoolMappingInput = Readonly<{
  leverId?: string | null;
  costPoolId?: string | null;
}>;

export type SgaCostPoolMappingReceipt = Readonly<{
  memberId: SgaCostPoolMemberId;
  reason: SgaCostPoolMappingReason;
  taxonomyVersion: typeof SGA_COST_POOL_TAXONOMY_VERSION;
  matchedOn: SgaCostPoolMappingInput;
}>;

const COST_POOL_ID_TO_MEMBER_ID: ReadonlyMap<string, SgaCostPoolLeverMemberId> = new Map(
  SGA_COST_POOL_LEVER_MEMBER_IDS.map((memberKey) => [
    SGA_COST_POOL_SOURCE_LINE_KEY_BY_LEVER[memberKey],
    memberKey,
  ]),
);
const LEVER_ID_SET: ReadonlySet<string> = new Set(SGA_COST_POOL_LEVER_MEMBER_IDS);

export function mapSgaSourceRowToCostPoolMember(
  input: SgaCostPoolMappingInput,
): SgaCostPoolMappingReceipt {
  const leverId = input.leverId ?? undefined;
  if (leverId && LEVER_ID_SET.has(leverId)) {
    return {
      memberId: leverId as SgaCostPoolLeverMemberId,
      reason: 'matched_lever_id',
      taxonomyVersion: SGA_COST_POOL_TAXONOMY_VERSION,
      matchedOn: input,
    };
  }

  const costPoolId = input.costPoolId ?? undefined;
  const matchedByPoolId = costPoolId ? COST_POOL_ID_TO_MEMBER_ID.get(costPoolId) : undefined;
  if (matchedByPoolId) {
    return {
      memberId: matchedByPoolId,
      reason: 'matched_cost_pool_id',
      taxonomyVersion: SGA_COST_POOL_TAXONOMY_VERSION,
      matchedOn: input,
    };
  }

  return {
    memberId: SGA_COST_POOL_UNALLOCATED_MEMBER_ID,
    reason: leverId || costPoolId ? 'unmapped_unknown_identity' : 'unmapped_no_source_identity',
    taxonomyVersion: SGA_COST_POOL_TAXONOMY_VERSION,
    matchedOn: input,
  };
}

/**
 * §1: ONE canonical SG&A spend measure with `sga_cost_pool` as a governed
 * dimension on it (no second metric identity for the per-pool projection - see
 * PR body §1 map). Aliased to the exact fixture spellings the drift-showcase
 * pack (PR #5543, unmerged) authored, so that PR resolves against this same
 * identity once it rebases rather than minting a second one.
 */
export const SGA_COST_POOL_CANONICAL_METRIC_KEY = 'sga_spend_run_rate_usd' as const;
export const SGA_COST_POOL_BASIS_METRIC_KEY = 'sga_spend_addressable_run_rate_usd' as const;

/** Reused by the metric_registry_v1 grain: sum-by-pool and enterprise-total. */
const SGA_COST_POOL_AGGREGATION_RULES: readonly MetricRegistryAggregationRule[] = [
  {
    dimension: SGA_COST_POOL_DIMENSION,
    rollup: 'sum',
    ruleFormulaId: 'additive_rollup_v1',
    materialization: 'computed_on_demand',
  },
  {
    dimension: SGA_COST_POOL_DIMENSION,
    rollup: 'enterprise',
    ruleFormulaId: 'additive_rollup_v1',
    materialization: 'computed_on_demand',
  },
];

const SGA_COST_POOL_APPLIES_TO: readonly MetricRegistryAppliesTo[] = [
  { dimension: SGA_COST_POOL_DIMENSION, members: 'all' },
];

/**
 * Matches the metricEntry() helper's accepted input shape in metric-registry.ts
 * (that helper is module-private, so this file cannot call it directly - the
 * MA-synergy-tracking data file established the same seed-input/`.map(metricEntry)`
 * split for exactly this reason).
 */
export interface SgaCostPoolMetricRegistrySeedInput {
  readonly metricKey: string;
  readonly label: string;
  readonly definition: string;
  readonly reconciliationRule: string;
  readonly source: string;
  readonly appliesTo: readonly MetricRegistryAppliesTo[];
  readonly unit: string;
  readonly frame: string;
  readonly direction: MetricDirection;
  readonly aggregationRules: readonly MetricRegistryAggregationRule[];
  readonly temporalType: MetricTemporalType;
  readonly aliases: readonly string[];
  readonly sourceOfTruthClass: MetricRegistrySourceOfTruthClass;
  readonly ownerModuleId: string;
  readonly sourceBindingKeys: readonly string[];
  readonly requiredEvidenceClasses: readonly string[];
  readonly benchmarkable: false;
  // Structural fix (#5734 measure-identity sweep): this seed-input shape previously
  // had no way to carry measureIdentity through to the metricEntry() builder it
  // maps over in metric-registry.ts, so the two SG&A cost-pool rows below were
  // structurally incapable of ever declaring one.
  readonly measureIdentity?: GovernedMeasureIdentity;
}

// Kept as a literal string, not an import: apps/api/src/ask-helix/drift/
// realization-component-variance-artifact-source.ts#REALIZATION_COMPONENT_VARIANCE_SCHEMA_VERSION
// (packages/lib may not import from apps/api). A conformance spec in that
// module pins this string against the real constant.
const REALIZATION_COMPONENT_VARIANCE_EVIDENCE_CLASS = 'realization_component_variance_v1';

export const SGA_COST_POOL_METRIC_REGISTRY_SEED_INPUTS: readonly SgaCostPoolMetricRegistrySeedInput[] =
  Object.freeze([
    {
      metricKey: SGA_COST_POOL_CANONICAL_METRIC_KEY,
      measureIdentity: {
        measuredDimension: 'operating_expense',
        valueForm: 'amount',
        unitFamily: 'currency',
        normalizationBasis: 'none',
      },
      label: 'SG&A run-rate spend, by cost pool',
      definition:
        'The canonical SG&A run-rate spend measure, observed weekly against the ' +
        'sga_cost_pool dimension. Aggregation across pool members is a strict sum of ' +
        'disjoint members plus the explicit unallocated_or_unmapped residual - never ' +
        'a second, independently defined SG&A spend total (#5544 AMEND §1).',
      reconciliationRule:
        'canonical total = sum(disjoint sga_cost_pool member rows) + unallocated_or_unmapped ' +
        'residual, per tenant x period, via the approved variance_decomposition_v1 formula.',
      source:
        'apps/api/src/ask-helix/drift/realization-component-variance-artifact-source.ts#' +
        'REALIZATION_COMPONENT_VARIANCE_SCHEMA_VERSION (#5544)',
      appliesTo: SGA_COST_POOL_APPLIES_TO,
      unit: 'USD',
      frame: 'sga_cost_pool_run_rate',
      direction: 'lower_is_better',
      aggregationRules: SGA_COST_POOL_AGGREGATION_RULES,
      temporalType: 'stock',
      aliases: ['sga_run_rate_usd'],
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'sga-spend',
      sourceBindingKeys: [SGA_COST_POOL_CANONICAL_METRIC_KEY],
      requiredEvidenceClasses: [REALIZATION_COMPONENT_VARIANCE_EVIDENCE_CLASS],
      benchmarkable: false,
    },
    {
      metricKey: SGA_COST_POOL_BASIS_METRIC_KEY,
      measureIdentity: {
        measuredDimension: 'operating_expense',
        valueForm: 'amount',
        unitFamily: 'currency',
        normalizationBasis: 'none',
      },
      label: 'SG&A addressable spend basis, by cost pool',
      definition:
        'The addressable-spend basis the SG&A run-rate rate effect is computed against, ' +
        'observed weekly against the same sga_cost_pool dimension as the canonical measure.',
      reconciliationRule:
        'Basis for the within-line rate and mix effects variance_decomposition_v1 computes ' +
        'for sga_spend_run_rate_usd; never summed as a spend figure on its own.',
      source:
        'apps/api/src/ask-helix/drift/realization-component-variance-artifact-source.ts#' +
        'REALIZATION_COMPONENT_VARIANCE_SCHEMA_VERSION (#5544)',
      appliesTo: SGA_COST_POOL_APPLIES_TO,
      unit: 'USD',
      frame: 'sga_cost_pool_run_rate',
      direction: 'context',
      aggregationRules: SGA_COST_POOL_AGGREGATION_RULES,
      temporalType: 'stock',
      aliases: ['sga_addressable_spend_usd'],
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'sga-spend',
      sourceBindingKeys: [SGA_COST_POOL_BASIS_METRIC_KEY],
      requiredEvidenceClasses: [REALIZATION_COMPONENT_VARIANCE_EVIDENCE_CLASS],
      benchmarkable: false,
    },
  ]);
